import React from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Crown, X } from "@phosphor-icons/react";

/**
 * Shown at the very top of every page when the current session is an admin
 * impersonating another user. Provides a single-tap "Return to admin" button
 * that swaps the cookie back to the admin's original session.
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const imp = user?.impersonated_by;
  if (!imp) return null;

  const stop = async () => {
    try {
      await api.post("/admin/stop-impersonate");
      toast.success("Returned to admin session");
      await refresh();
      window.location.href = "/admin";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to stop impersonation");
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="w-full bg-destructive text-destructive-foreground border-b-4 border-foreground px-4 py-2 flex items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-wider sticky top-0 z-[500]"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Crown size={14} weight="fill" />
        <span className="truncate">
          Impersonating <strong>{user.name || user.email}</strong>
          <span className="opacity-70"> · admin {imp.admin_email || imp.admin_id}</span>
        </span>
      </div>
      <button
        type="button"
        onClick={stop}
        data-testid="impersonation-stop-btn"
        className="inline-flex items-center gap-1 bg-background text-foreground border-2 border-foreground px-2 py-1 hover:bg-foreground hover:text-background transition-colors"
      >
        <X size={12} weight="bold" /> Return to admin
      </button>
    </div>
  );
}
